import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { ExternalLink } from 'lucide-react';
import {
    memo,
    type KeyboardEvent,
    type ReactElement,
    type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';
import type { PatientSummaryDto } from '~/backend/connector-next-api/connector-next-schemas';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { SeparatorDot } from '~/components/Separated';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import type { OpenInViewer } from '~/hooks/openInViewer';
import {
    AssigneeAvatar,
    CourseStageStrip,
    ImportActivityChip,
    PatientIdText,
    PresenceChip,
} from '../shared';
import ModalityChip from '~/components/Chip/ModalityChip';
import {
    ageInYears,
    formatDayText,
    formatPatientName,
    patientAssignee,
    patientImageModalities,
    patientImportActivity,
    patientCourseStagesDone,
    patientRtObjects,
    resolveCourseStage,
    sexAgeText,
} from '../model';
import {
    PatientTableRow,
    visiblePatientListColumns,
    PatientListBodyCell,
    type PatientListColumnId,
} from './PatientListTable';

type PatientRowProps = {
    patient: PatientSummaryDto;
    compact: boolean;
    selected: boolean;
    onSelect: (id: string) => void;
    onOpenInViewer: OpenInViewer;
    /** True while another open is in flight; the open control is disabled. */
    openBusy: boolean;
};

/** Width cap of the course-stage strip in a wide row. */
const COURSE_STAGE_STRIP_MAX_WIDTH = 760;

/** Mono meta line under a name: ID, sex and age; site and assignee in compact mode. */
const META_LINE_SX = {
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    minWidth: 0,
    fontFamily: 'fontFamilyMonospace',
    fontSize: 12,
    color: 'text.secondary',
    whiteSpace: 'nowrap',
} as const;

const PatientRow = memo(function PatientRow({
    patient,
    compact,
    selected,
    onSelect,
    onOpenInViewer,
    openBusy,
}: PatientRowProps) {
    const intl = useIntl();
    const stage = resolveCourseStage(patient.courseStage);
    const stagesDone = patientCourseStagesDone(patient.courseStagesDone);
    const assignee = patientAssignee(patient);
    const modalities = patientImageModalities(patient);
    const rtObjects = patientRtObjects(patient);
    const sexAge = sexAgeText(
        intl,
        patient.sex,
        ageInYears(patient.birthDate, new Date())
    );
    const lastActivity = formatDayText(intl, patient.lastActivityAt);
    // Rows without a backend id cannot open (or close) the panel.
    const patientId = patient.id;
    const onOpen =
        patientId !== undefined ? () => onSelect(patientId) : undefined;
    // The hover icon opens every dataset straight in the viewer, bypassing the
    // panel; the row body still expands the drilldown.
    const openThisPatient =
        patientId !== undefined && !openBusy
            ? () => onOpenInViewer(patientId, 'all')
            : undefined;

    const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.currentTarget !== event.target) {
            return;
        }
        if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onOpen();
        }
    };

    const presence = (
        <PresenceChip
            activeEditorCount={patient.activeEditorCount}
            activeEditingCount={patient.activeEditingCount}
        />
    );

    const datasetChips = (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                minWidth: 0,
            }}
        >
            {modalities.map(([modality, count]) => (
                <ModalityChip
                    key={modality}
                    modality={modality}
                    count={count}
                />
            ))}
            {rtObjects.map((label) => (
                <ModalityChip key={label} modality={label} />
            ))}
            <ImportActivityChip {...patientImportActivity(patient)} />
        </Box>
    );

    // One cell per column id: the Record makes a missing or extra cell a
    // compile error, and PATIENT_LIST_COLUMNS order keeps body and header aligned.
    const cells: Record<PatientListColumnId, ReactNode> = {
        patient: (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '3px',
                    minWidth: 0,
                }}
            >
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        display: 'block',
                        minWidth: 0,
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.25,
                        color: 'text.primary',
                    }}
                >
                    <SensitiveInfoTypography>
                        {formatPatientName(patient.dicomPatientName) || '-'}
                    </SensitiveInfoTypography>
                </Typography>
                <Box sx={META_LINE_SX}>
                    <PatientIdText
                        id={patient.dicomPatientId}
                        tooltipPlacement="right"
                    />
                    {sexAge && (
                        <>
                            <SeparatorDot />
                            <SensitiveInfoTypography>
                                {sexAge}
                            </SensitiveInfoTypography>
                        </>
                    )}
                </Box>
                {/* Only with something to fold in: an empty line still takes
                    the column's gap and grows the row as the panel opens. */}
                {compact && assignee && (
                    <Box
                        sx={{
                            ...META_LINE_SX,
                            fontFamily: 'inherit',
                            overflow: 'hidden',
                        }}
                    >
                        <Typography component="span" noWrap fontSize={13}>
                            {assignee}
                        </Typography>
                    </Box>
                )}
            </Box>
        ),
        workflow: compact ? (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                {presence && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            minWidth: 0,
                        }}
                    >
                        {presence}
                    </Box>
                )}
                {/* Unnamed: the patient panel beside the compact list names
                    the stages of the patient it shows. */}
                <CourseStageStrip
                    stage={stage}
                    done={stagesDone}
                    labels={false}
                />
            </Box>
        ) : (
            // Capped so the strip reads much the same beside an open patient
            // as without one.
            <Box sx={{ maxWidth: COURSE_STAGE_STRIP_MAX_WIDTH }}>
                <CourseStageStrip stage={stage} done={stagesDone} size="row" />
            </Box>
        ),
        assignee: (
            <Box
                component="span"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <AssigneeAvatar name={assignee} />
                <Typography
                    component="span"
                    noWrap
                    sx={{
                        display: 'block',
                        minWidth: 0,
                        fontSize: 13,
                        color: 'text.secondary',
                    }}
                >
                    {assignee ?? ''}
                </Typography>
            </Box>
        ),
        datasets: datasetChips,
        lastActivity: (
            <>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '3px',
                    }}
                >
                    <Typography
                        variant="monoMeta"
                        sx={{ fontSize: 12, color: 'text.primary' }}
                    >
                        {lastActivity}
                    </Typography>
                    {presence}
                </Box>
                <Box
                    className="patient-list-open"
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pr: 2,
                        pl: 6,
                        // The stop must be opaque to occlude the cell text;
                        // the neutral lift matches an unselected hovered row.
                        background: (theme) =>
                            `linear-gradient(to right, transparent, ${theme.palette.action.hoverNeutral} 42%)`,
                        opacity: 0,
                        transition: 'opacity 120ms',
                        pointerEvents: 'none',
                    }}
                >
                    <OpenButtonHint
                        enabled={!!openThisPatient}
                        title={intl.formatMessage({
                            id: openBusy
                                ? 'patientList.open.inFlight'
                                : 'patientList.openHint',
                        })}
                    >
                        <ButtonBase
                            disabled={!openThisPatient}
                            aria-label={intl.formatMessage({
                                id: 'patientList.row.openPatient',
                            })}
                            onClick={(event) => {
                                event.stopPropagation();
                                openThisPatient?.();
                            }}
                            sx={{
                                display: 'inline-flex',
                                width: 32,
                                height: 32,
                                borderRadius: 1,
                                color: 'text.secondary',
                                '&.Mui-disabled': {
                                    color: 'text.secondary',
                                },
                            }}
                        >
                            <ExternalLink size={16} />
                        </ButtonBase>
                    </OpenButtonHint>
                </Box>
            </>
        ),
    };

    return (
        <PatientTableRow
            isSelected={selected}
            tabIndex={onOpen ? 0 : undefined}
            aria-expanded={onOpen ? selected : undefined}
            onClick={onOpen}
            onKeyDown={onKeyDown}
        >
            {visiblePatientListColumns(compact).map((column) => (
                <PatientListBodyCell key={column.id} alignEnd={column.alignEnd}>
                    {cells[column.id]}
                </PatientListBodyCell>
            ))}
        </PatientTableRow>
    );
});

/**
 * Hint around the hover-open button. Disabled needs `DisabledHint`: a
 * disabled button is not focusable, so without its focusable span the row's
 * focus-within reveal never fires and the hint is pointer-only.
 */
function OpenButtonHint({
    enabled,
    title,
    children,
}: {
    enabled: boolean;
    title: string;
    children: ReactElement;
}) {
    return enabled ? (
        <Tooltip describeChild title={title} placement="left">
            {children}
        </Tooltip>
    ) : (
        <DisabledHint title={title} placement="left">
            {children}
        </DisabledHint>
    );
}

export default PatientRow;
